import { useEffect, useState } from "react";
import {
  ArrowRight,
  Coins,
  Clock3,
  Gift,
  Leaf,
  MapPin,
  MessageCircle,
  Search,
  ShieldCheck,
  ShoppingBasket,
  Sparkles,
  Store,
  Truck,
} from "lucide-react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { ProductCard } from "../components/ProductCard";
import {
  Button,
  EmptyState,
  ErrorState,
  MediaImage,
  Notice,
  Price,
  QuantitySelector,
  Skeleton,
  StatusBadge,
} from "../components/ui";
import { useCart } from "../context/CartContext";
import { useStore } from "../context/StoreContext";
import { apiRequest, queryString } from "../lib/api";
import { orderHomepageCategories } from "../lib/category-order";
import { formatRupiah } from "../lib/format";
import { branchWhatsAppUrl } from "../lib/whatsapp";
import {
  normalizeProductDetail,
  normalizeProductCollection,
  type Product,
} from "../lib/types";
import {
  MEMBER_POINT_VALUE_RUPIAH,
  MEMBER_SPEND_PER_POINT_RUPIAH,
} from "../../shared/loyalty";

function useProducts(filters: {
  query?: string;
  category?: string;
  page?: number;
  limit?: number;
  sort?: "featured" | "newest" | "price_asc" | "price_desc";
  includeTotal?: boolean;
}) {
  const { activeBranchId, store } = useStore();
  const query = filters.query;
  const category = filters.category;
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>();
  const [total, setTotal] = useState(0);

  useEffect(() => {
    let active = true;
    const timer = window.setTimeout(() => {
      setLoading(true);
      setError(undefined);
      const hasBootstrapProducts =
        !query && !category && (store?.products.length ?? 0) > 0;
      if (hasBootstrapProducts) {
        setProducts(store?.products ?? []);
        setLoading(false);
        return;
      }
      void apiRequest<unknown>(
        `/api/public/products${queryString({
          branch_id:
            activeBranchId === undefined ? undefined : String(activeBranchId),
          search: query,
          category,
          page: filters.page,
          limit: filters.limit ?? 48,
          sort: filters.sort,
          include_total: filters.includeTotal === false ? 0 : undefined,
        })}`,
      )
        .then((payload) => {
          if (active) {
            const collection = normalizeProductCollection(payload);
            setProducts(collection.items);
            setTotal(collection.total);
          }
        })
        .catch((caught) => {
          if (active)
            setError(
              caught instanceof Error
                ? caught.message
                : "Produk belum dapat dimuat.",
            );
        })
        .finally(() => {
          if (active) setLoading(false);
        });
    }, 0);
    return () => {
      active = false;
      window.clearTimeout(timer);
    };
  }, [
    activeBranchId,
    category,
    filters.limit,
    filters.page,
    filters.sort,
    filters.includeTotal,
    query,
    store?.products,
  ]);

  return { products, loading, error, total };
}

export function HomePage() {
  const { store } = useStore();
  const [params] = useSearchParams();
  const query = params.get("q")?.trim() ?? "";
  const { products, loading, error } = useProducts({
    query,
    includeTotal: false,
  });
  const activeBranch = store?.activeBranch;
  const activeBranchWhatsApp = branchWhatsAppUrl(
    activeBranch?.whatsappNumber,
    activeBranch?.name,
  );
  const deliveryBranch = store?.branches.find(
    (branch) => branch.delivery.deliveryEnabled,
  );
  const homepageCategories = orderHomepageCategories(
    store?.navigationCategories ?? [],
    store?.categories ?? [],
  );

  const sections = (() => {
    if (query) return [];
    if (store?.sections.length) {
      return store.sections
        .map((section) => {
          const sectionProducts =
            section.type === "CUSTOM_PRODUCTS"
              ? (section.products ?? [])
              : section.products?.length
                ? section.products
                : products.filter((product) =>
                    section.type === "PROMO_PRODUCTS"
                      ? product.promoPrice !== undefined
                      : section.categoryId !== undefined
                        ? String(product.categoryId) ===
                          String(section.categoryId)
                        : product.isFeatured,
                  );
          return { ...section, products: sectionProducts.slice(0, 8) };
        })
        .filter((section) =>
          section.type === "BANNER"
            ? section.mediaId !== undefined
            : (section.products?.length ?? 0) > 0,
        );
    }
    return [
      {
        id: "pilihan",
        title: "Pilihan Segar Hari Ini",
        type: "FEATURED_PRODUCTS",
        products: products.slice(0, 8),
      },
    ];
  })();

  return (
    <div className="home-page">
      {!query && (
        <section className="hero page-shell">
          <div className="hero__content">
            <span className="eyebrow">
              <Sparkles aria-hidden="true" size={18} /> Segar untuk keluarga
            </span>
            <h1>SedapSayur — Warung Sedap Sayur</h1>
            <p>
              <strong>Lengkap, Murah, Berkualitas.</strong> Belanja sayur,
              daging, bumbu, dan sembako dari cabang pilihan Anda. Jadi member
              agar setiap belanja makin hemat dan poinnya terus bertambah.
            </p>
            <div className="hero__actions">
              <button
                className="button button--primary"
                type="button"
                onClick={() => {
                  document
                    .getElementById("kategori-belanja")
                    ?.scrollIntoView({ behavior: "auto", block: "start" });
                }}
              >
                <ShoppingBasket aria-hidden="true" size={19} />
                <span>Mulai Belanja</span>
              </button>
              <Link className="button button--secondary" to="/member">
                <Gift aria-hidden="true" size={19} />
                <span>Daftar Member</span>
              </Link>
              {activeBranchWhatsApp && (
                <a
                  className="button button--secondary"
                  target="_blank"
                  rel="noreferrer"
                  href={activeBranchWhatsApp}
                >
                  <MessageCircle aria-hidden="true" size={19} />
                  <span>Tanya via WhatsApp</span>
                </a>
              )}
            </div>
            <div className="hero__trust">
              <span>
                <Leaf aria-hidden="true" /> Pilihan segar
              </span>
              <span>
                <ShieldCheck aria-hidden="true" /> Harga sesuai cabang
              </span>
              <span>
                <Coins aria-hidden="true" />{" "}
                {formatRupiah(MEMBER_SPEND_PER_POINT_RUPIAH)} = 1 poin ={" "}
                {formatRupiah(MEMBER_POINT_VALUE_RUPIAH)}
              </span>
            </div>
          </div>
          <div className="hero__visual" aria-label="Informasi cabang aktif">
            <div className="hero-store-card">
              <span className="hero-store-card__icon">
                <Store aria-hidden="true" />
              </span>
              <span>Anda berbelanja di</span>
              <strong>{activeBranch?.name}</strong>
              <p>{activeBranch?.address}</p>
              <small>
                Buka {activeBranch?.openingTime}–{activeBranch?.closingTime}
              </small>
              {activeBranch && <StatusBadge status={activeBranch.status} />}
            </div>
            <div className="produce-orbit produce-orbit--one">🥬</div>
            <div className="produce-orbit produce-orbit--two">🥕</div>
            <div className="produce-orbit produce-orbit--three">🧅</div>
          </div>
        </section>
      )}

      {!query && (
        <section className="member-marketing page-shell" aria-labelledby="member-marketing-title">
          <span className="member-marketing__icon">
            <Gift aria-hidden="true" />
          </span>
          <div>
            <span className="eyebrow eyebrow--light">Lebih segar, lebih untung</span>
            <h2 id="member-marketing-title">
              Belanja untuk keluarga, kumpulkan poin untuk belanja berikutnya
            </h2>
            <p>
              Jadilah Member Sedap Sayur. Setiap belanja{" "}
              {formatRupiah(MEMBER_SPEND_PER_POINT_RUPIAH)} mendapatkan 1 poin.
              Setiap 1 poin bernilai {formatRupiah(MEMBER_POINT_VALUE_RUPIAH)}{" "}
              untuk potongan belanja. Kumpulkan terus untuk hemat seperti
              cashback dan nikmati hadiah menarik di toko.
            </p>
          </div>
          <Link className="button member-marketing__button" to="/member">
            Daftar Member Gratis <ArrowRight aria-hidden="true" />
          </Link>
        </section>
      )}

      {!query && (
        <section
          className="page-shell category-section"
          id="kategori-belanja"
          aria-labelledby="category-title"
        >
          <div className="section-heading">
            <div>
              <span className="eyebrow">Cari berdasarkan kebutuhan</span>
              <h2 id="category-title">Belanja Berdasarkan Kategori</h2>
            </div>
          </div>
          <div className="category-grid">
            {homepageCategories.map((category) => (
              <Link
                className="category-card"
                to={`/kategori/${category.slug}`}
                key={String(category.id)}
              >
                <MediaImage mediaId={category.mediaId} alt={category.name} />
                <span>{category.name}</span>
                <ArrowRight aria-hidden="true" size={18} />
              </Link>
            ))}
          </div>
        </section>
      )}

      {!query && (
        <section
          className="page-shell business-info-section"
          aria-labelledby="business-info-title"
        >
          <div className="section-heading">
            <div>
              <span className="eyebrow">Informasi Usaha</span>
              <h2 id="business-info-title">Cabang & waktu pelayanan</h2>
              <p>
                Pilih cabang untuk melihat harga dan stok yang tersedia secara
                langsung.
              </p>
            </div>
          </div>
          <div className="business-info-layout">
            <div className="business-branch-grid">
              {store?.branches.map((branch) => (
                <article className="business-branch-card" key={String(branch.id)}>
                  <div className="business-branch-card__heading">
                    <span className="business-branch-card__icon">
                      <Store aria-hidden="true" />
                    </span>
                    <div>
                      <small>Cabang {branch.isDefault ? "utama" : ""}</small>
                      <h3>{branch.name}</h3>
                    </div>
                  </div>
                  <p>
                    <MapPin aria-hidden="true" />
                    <span>{branch.address}</span>
                  </p>
                  <p>
                    <Clock3 aria-hidden="true" />
                    <span>
                      Jam buka {branch.openingTime}–{branch.closingTime}{" "}
                      {branch.delivery.timeZoneLabel}
                    </span>
                  </p>
                  <span
                    className={`delivery-availability ${branch.delivery.deliveryEnabled ? "delivery-availability--active" : ""}`}
                  >
                    <Truck aria-hidden="true" />
                    {branch.delivery.deliveryEnabled
                      ? "Pengiriman aktif"
                      : "Pengiriman belum aktif"}
                  </span>
                </article>
              ))}
            </div>
            {deliveryBranch && (
              <aside className="delivery-business-card">
                <span className="eyebrow">Pengiriman dari cabang</span>
                <h3>{deliveryBranch.name}</h3>
                <p>{deliveryBranch.address}</p>
                <div className="delivery-window-list">
                  {deliveryBranch.delivery.deliveryWindows.map((window) => (
                    <div key={`${window.label}-${window.startTime}`}>
                      <Clock3 aria-hidden="true" />
                      <span>
                        <strong>
                          {window.startTime}–{window.endTime}{" "}
                          {deliveryBranch.delivery.timeZoneLabel}
                        </strong>
                        <small>
                          {window.label} · last order pukul {window.cutoffTime}
                        </small>
                      </span>
                    </div>
                  ))}
                </div>
                {deliveryBranch.delivery.freeDeliveryMinimum && (
                  <div className="free-delivery-callout">
                    <Truck aria-hidden="true" />
                    <span>
                      <strong>
                        Gratis ongkir di {deliveryBranch.delivery.deliveryArea ?? "Area Denpasar"}
                      </strong>
                      <small>
                        Minimal belanja {formatRupiah(
                          deliveryBranch.delivery.freeDeliveryMinimum,
                        )}
                      </small>
                    </span>
                  </div>
                )}
              </aside>
            )}
          </div>
        </section>
      )}

      <section
        className="page-shell product-section"
        id="produk-pilihan"
        aria-labelledby="product-title"
      >
        {query && (
          <div className="search-result-heading">
            <span className="eyebrow">
              <Search aria-hidden="true" size={18} /> Hasil pencarian
            </span>
            <h1 id="product-title">{`Produk untuk “${query}”`}</h1>
            <p>
              {loading
                ? "Sedang mencari…"
                : `${products.length} produk ditemukan di ${activeBranch?.name}.`}
            </p>
          </div>
        )}
        {loading && <Skeleton rows={8} cards />}
        {error && <ErrorState message={error} />}
        {!loading && !error && query && products.length === 0 && (
          <EmptyState
            title="Produk tidak ditemukan"
            description="Coba kata yang lebih singkat atau lihat semua kategori."
            action={
              <Button
                variant="secondary"
                onClick={() => window.location.assign("/")}
              >
                Lihat Semua Kategori
              </Button>
            }
          />
        )}
        {!loading && !error && query && products.length > 0 && (
          <div className="product-grid">
            {products.map((product) => (
              <ProductCard product={product} key={String(product.id)} />
            ))}
          </div>
        )}
        {!loading &&
          !error &&
          !query &&
          sections.map((section, index) => {
            if (section.type === "BANNER" && section.mediaId !== undefined) {
              return (
                <article className="homepage-banner" key={String(section.id)}>
                  <MediaImage mediaId={section.mediaId} alt={section.title} />
                  <div>
                    <span className="eyebrow">Informasi Warung Sedap Sayur</span>
                    <h2>{section.title}</h2>
                    {section.subtitle && <p>{section.subtitle}</p>}
                    {section.ctaUrl && (
                      <Link
                        className="button button--primary"
                        to={section.ctaUrl}
                      >
                        {section.ctaText || "Lihat Selengkapnya"}
                      </Link>
                    )}
                  </div>
                </article>
              );
            }
            return (
              <div className="home-product-block" key={String(section.id)}>
                <div className="section-heading">
                  <div>
                    <span className="eyebrow">
                      {index === 0
                        ? "Pilihan untuk Anda"
                        : "Belanja lebih mudah"}
                    </span>
                    <h2 id={index === 0 ? "product-title" : undefined}>
                      {section.title}
                    </h2>
                    {section.subtitle && <p>{section.subtitle}</p>}
                  </div>
                  {section.categoryId && (
                    <Link
                      to={`/kategori/${store?.categories.find((category) => String(category.id) === String(section.categoryId))?.slug ?? ""}`}
                    >
                      Lihat Semua <ArrowRight aria-hidden="true" size={18} />
                    </Link>
                  )}
                </div>
                <div className="product-grid">
                  {section.products?.map((product) => (
                    <ProductCard product={product} key={String(product.id)} />
                  ))}
                </div>
              </div>
            );
          })}
      </section>
    </div>
  );
}

export function CategoryPage() {
  const { slug = "" } = useParams();
  const { store } = useStore();
  const [sort, setSort] = useState("default");
  const [pageState, setPageState] = useState({ slug, page: 1 });
  const page = pageState.slug === slug ? pageState.page : 1;
  const sortValue =
    sort === "low"
      ? "price_asc"
      : sort === "high"
        ? "price_desc"
        : "featured";
  const { products, loading, error, total } = useProducts({
    category: slug,
    page,
    limit: 30,
    sort: sortValue,
  });
  const category =
    store?.allCategories.find((item) => item.slug === slug) ??
    store?.categories.find((item) => item.slug === slug) ??
    store?.navigationCategories.find((item) => item.slug === slug);
  const totalPages = Math.max(1, Math.ceil(total / 30));
  return (
    <div className="page-shell inner-page">
      <nav className="breadcrumbs" aria-label="Breadcrumb">
        <Link to="/">Beranda</Link>
        <span>/</span>
        <span aria-current="page">{category?.name ?? "Kategori"}</span>
      </nav>
      <div className="catalog-heading">
        <div>
          <span className="eyebrow">Belanja mudah</span>
          <h1>{category?.name ?? "Kategori"}</h1>
          <p>
            {category?.description ??
              `Pilihan produk ${category?.name ?? ""} dari cabang aktif.`}
          </p>
        </div>
        <label className="compact-field">
          Urutkan
          <select
            value={sort}
            onChange={(event) => {
              setSort(event.target.value);
              setPageState({ slug, page: 1 });
            }}
          >
            <option value="default">Pilihan utama</option>
            <option value="low">Harga termurah</option>
            <option value="high">Harga tertinggi</option>
          </select>
        </label>
      </div>
      {loading && <Skeleton rows={8} cards />}
      {error && <ErrorState message={error} />}
      {!loading && !error && products.length === 0 && (
        <EmptyState
          title="Belum ada produk di kategori ini"
          description="Silakan lihat kategori lain atau hubungi cabang untuk bantuan."
          action={
            <Link className="button button--secondary" to="/">
              Lihat Kategori Lain
            </Link>
          }
        />
      )}
      {!loading && !error && (
        <div className="product-grid">
          {products.map((product) => (
            <ProductCard product={product} key={String(product.id)} />
          ))}
        </div>
      )}
      {!loading && !error && products.length > 0 && totalPages > 1 && (
        <nav className="catalog-pagination" aria-label="Halaman kategori">
          {Array.from({ length: totalPages }, (_, index) => index + 1).map(
            (item) => (
              <button
                type="button"
                className={item === page ? "active" : ""}
                aria-current={item === page ? "page" : undefined}
                onClick={() => setPageState({ slug, page: item })}
                key={item}
              >
                {item}
              </button>
            ),
          )}
          <button
            type="button"
            disabled={page >= totalPages}
            onClick={() => setPageState({ slug, page: page + 1 })}
          >
            Next <ArrowRight aria-hidden="true" size={16} />
          </button>
        </nav>
      )}
    </div>
  );
}

export function ProductDetailPage() {
  const { slug = "" } = useParams();
  const { activeBranchId, store } = useStore();
  const cart = useCart();
  const [product, setProduct] = useState<Product>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>();
  useEffect(() => {
    const timer = window.setTimeout(() => {
      setLoading(true);
      setError(undefined);
      void apiRequest<unknown>(
        `/api/public/products/${encodeURIComponent(slug)}${queryString({ branch_id: activeBranchId === undefined ? undefined : String(activeBranchId) })}`,
      )
        .then((payload) => setProduct(normalizeProductDetail(payload)))
        .catch((caught) =>
          setError(
            caught instanceof Error
              ? caught.message
              : "Produk belum dapat dimuat.",
          ),
        )
        .finally(() => setLoading(false));
    }, 0);
    return () => window.clearTimeout(timer);
  }, [activeBranchId, slug]);
  const item = product
    ? cart.items.find(
        (entry) => String(entry.product.id) === String(product.id),
      )
    : undefined;
  if (loading)
    return (
      <div className="page-shell inner-page">
        <Skeleton rows={4} cards />
      </div>
    );
  if (error || !product)
    return (
      <div className="page-shell inner-page">
        <ErrorState message={error ?? "Produk tidak ditemukan."} />
      </div>
    );
  const available =
    product.isAvailable && (product.isUnlimited || product.stock > 0);
  return (
    <div className="page-shell inner-page">
      <nav className="breadcrumbs" aria-label="Breadcrumb">
        <Link to="/">Beranda</Link>
        <span>/</span>
        <Link
          to={product.categorySlug ? `/kategori/${product.categorySlug}` : "/"}
        >
          {product.categoryName ?? "Produk"}
        </Link>
        <span>/</span>
        <span aria-current="page">{product.name}</span>
      </nav>
      <article className="product-detail">
        <div className="product-detail__media">
          <MediaImage
            mediaId={product.mediaId}
            alt={product.name}
            loading="eager"
            fetchPriority="high"
          />
        </div>
        <div className="product-detail__content">
          <span className="eyebrow">
            {product.categoryName ?? "Kebutuhan harian"}
          </span>
          <h1>{product.name}</h1>
          <p className="product-detail__unit">Dijual per {product.unit}</p>
          {product.description?.trim() && (
            <p className="product-detail__description">
              {product.description}
            </p>
          )}
          <Price
            price={product.price}
            promoPrice={product.promoPrice}
            size="large"
          />
          <div className="availability-card">
            <span>
              <strong>{available ? "Tersedia" : "Habis"}</strong>
              <small>
                {product.isUnlimited && available
                  ? "Stok selalu tersedia"
                  : available
                  ? `Sisa stok: ${product.stock}`
                  : "Silakan pilih produk lain"}
              </small>
            </span>
            <StatusBadge status={available ? "active" : "inactive"} />
          </div>
          <div className="branch-inline">
            <Store aria-hidden="true" />
            <span>
              <small>Harga dan stok dari</small>
              <strong>{store?.activeBranch?.name}</strong>
            </span>
          </div>
          {item ? (
            <QuantitySelector
              value={item.quantity}
              max={product.isUnlimited ? 99 : product.stock}
              onChange={(value) => cart.setQuantity(product.id, value)}
              label={product.name}
            />
          ) : (
            <Button
              disabled={!available || activeBranchId === undefined}
              onClick={() =>
                activeBranchId !== undefined &&
                cart.addItem(product, activeBranchId)
              }
              icon={<ShoppingBasket aria-hidden="true" />}
            >
              {available ? "Tambah ke Keranjang" : "Stok Habis"}
            </Button>
          )}
        </div>
      </article>
    </div>
  );
}

export function CartPage() {
  const cart = useCart();
  const { store } = useStore();
  if (cart.items.length === 0) {
    return (
      <div className="page-shell inner-page">
        <EmptyState
          title="Keranjang Anda masih kosong"
          description="Tambahkan kebutuhan keluarga dari katalog Warung Sedap Sayur."
          action={
            <Link className="button button--primary" to="/">
              Mulai Belanja
            </Link>
          }
        />
      </div>
    );
  }
  return (
    <div className="page-shell inner-page">
      <div className="page-heading">
        <span className="eyebrow">Hampir selesai</span>
        <h1>Keranjang Belanja</h1>
        <p>Harga dan stok akan diperiksa kembali sebelum pesanan dibuat.</p>
      </div>
      <div className="cart-layout">
        <section className="cart-list" aria-label="Isi keranjang">
          <Notice tone="info">
            Keranjang ini khusus untuk{" "}
            <strong>{store?.activeBranch?.name}</strong>.
          </Notice>
          {cart.items.map((item) => (
            <article className="cart-item" key={String(item.product.id)}>
              <MediaImage
                mediaId={item.product.mediaId}
                alt={item.product.name}
              />
              <div className="cart-item__content">
                <Link to={`/produk/${item.product.slug}`}>
                  {item.product.name}
                </Link>
                <span>per {item.product.unit}</span>
                <Price
                  price={item.product.price}
                  promoPrice={item.product.promoPrice}
                />
              </div>
              <div className="cart-item__actions">
                <QuantitySelector
                  value={item.quantity}
                  max={item.product.isUnlimited ? 99 : item.product.stock}
                  onChange={(value) => cart.setQuantity(item.product.id, value)}
                  label={item.product.name}
                />
                <strong>
                  {formatRupiah(
                    (item.product.promoPrice ?? item.product.price) *
                      item.quantity,
                  )}
                </strong>
                <button
                  type="button"
                  onClick={() => cart.removeItem(item.product.id)}
                >
                  Hapus
                </button>
              </div>
            </article>
          ))}
        </section>
        <aside className="order-summary">
          <h2>Ringkasan</h2>
          <div>
            <span>Jumlah barang</span>
            <strong>{cart.itemCount}</strong>
          </div>
          <div>
            <span>Subtotal</span>
            <strong>{formatRupiah(cart.subtotal)}</strong>
          </div>
          <p>Ongkir dihitung setelah Anda memilih cara menerima pesanan.</p>
          <Link className="button button--primary button--full" to="/checkout">
            Lanjut ke Pemesanan <ArrowRight aria-hidden="true" />
          </Link>
          <Link className="text-link" to="/">
            Lanjut Belanja
          </Link>
        </aside>
      </div>
    </div>
  );
}
